import {
  Box,
  Button,
  FormControlLabel,
  IconButton,
  Radio,
  RadioGroup,
  Typography,
} from '@mui/material'
import { FC, useState } from 'react'
import { FFModal } from '~/shared/ui'
import CloseIcon from '@mui/icons-material/Close'
import { appointmentCancelV2, bffCrmPatientAppointments } from '@fireflyhealth/api'
import { useQueryClient } from '@tanstack/react-query'
import { useSnackbar } from 'notistack'
import { usePatientDetail } from '~/features/member-profile'
import { fireAndForget } from '~/shared/lib/fireAndForget'

const RescheduleMapping = {
  now: 'Yes, reschedule now',
  rescheduled: 'Yes, already rescheduled',
  patient_to_reschedule: 'I’ve asked the patient to reschedule',
  cancel: 'No, just cancel',
}

const ConfirmCancelApptModal: FC<{
  // Only the appointment `id` (cancel path param) and `patient` (cache-invalidation key) are read.
  appointment: { id: number; patient: number }
  onClose: (continueToBooking: boolean) => void
  cancellationReasonIds: Array<number>
  otherReasonForPatient: string
  otherReasonForFirefly: string
}> = props => {
  const [radioButtonValue, setRadioButtonValue] = useState<string>('')
  const { enqueueSnackbar } = useSnackbar()
  const client = useQueryClient()
  const [saving, setSaving] = useState<boolean>(false)
  const { id: patientId } = usePatientDetail()

  const handleConfirmCancellation = async () => {
    setSaving(true)
    const body = {
      cancellation_reason_ids: props.cancellationReasonIds,
      patient_other_reason: props.otherReasonForPatient,
      firefly_other_reason: props.otherReasonForFirefly,
      ...(radioButtonValue !== 'cancel' && { reschedule_action: radioButtonValue }),
    }

    const { data } = await appointmentCancelV2.create({ id: props.appointment.id }, { data: body })
    if (data.id) {
      enqueueSnackbar(`Appointment cancelled successfully`, { variant: 'success' })
    } else {
      enqueueSnackbar(`Failed to cancel the appointment`, { variant: 'error' })
    }
    await client.invalidateQueries({
      queryKey: bffCrmPatientAppointments.retrieveQueryKey({
        patient_id: props.appointment.patient,
      }),
    })
    await client.invalidateQueries({ queryKey: ['useAppointmentLogs', props.appointment.patient] })
    setSaving(false)
    props.onClose(radioButtonValue === 'now')
  }

  if (!patientId) return null

  return (
    <FFModal open={true} style={{ width: '65%', marginLeft: 'auto', marginRight: 'auto' }}>
      <>
        <Box sx={{ m: 2 }}>
          <Typography variant="h6">{'Confirm Cancellation'}</Typography>
        </Box>
        <Box sx={{ m: 2 }}>
          <Typography variant="subtitle1">
            {'This appointment will be cancelled. Will it be rescheduled?'}
          </Typography>
          <Box>
            <RadioGroup
              value={radioButtonValue}
              onChange={e => {
                setRadioButtonValue(e.target.value)
              }}
            >
              {Object.entries(RescheduleMapping).map(([key, label]) => (
                <FormControlLabel
                  key={key}
                  value={key}
                  control={<Radio />}
                  label={<Typography>{label}</Typography>}
                />
              ))}
            </RadioGroup>
          </Box>
        </Box>
        <Box sx={{ m: 2 }}>
          <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'flex-end' }}>
            <Button
              onClick={() => props.onClose(false)}
              variant="text"
              size="small"
              sx={{
                borderRadius: 10,
                fontSize: '1.4rem',
                maxHeight: '75%',
                width: '20%',
                marginRight: 1,
              }}
              disabled={saving}
            >
              <Typography sx={{ fontSize: '1.4rem', fontWeight: 500 }}>{'Exit'}</Typography>
            </Button>
            <Button
              disabled={radioButtonValue == '' || saving}
              variant="contained"
              size="small"
              onClick={() => {
                fireAndForget('[Appointments] confirm cancellation', handleConfirmCancellation)
              }}
              sx={{
                borderRadius: 10,
                fontSize: '1.4rem',
                maxHeight: '75%',
                width: '45%',
              }}
            >
              <Typography sx={{ fontSize: '1.4rem', fontWeight: 500 }}>
                {'Confirm Cancellation'}
              </Typography>
            </Button>
          </Box>
        </Box>

        <Box sx={{ m: 2 }}>
          <IconButton
            onClick={() => props.onClose(false)}
            sx={{
              position: 'absolute',
              zIndex: 100,
              top: 2,
              right: 2,
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </>
    </FFModal>
  )
}

export default ConfirmCancelApptModal
